import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export interface TokenPagedResult<TItem> {
  items: TItem[];
  nextToken?: string | null;
}

interface UseTokenPagedInfiniteQueryOptions<TItem> {
  queryKey: readonly unknown[];
  fetchPage: (nextToken?: string) => Promise<TokenPagedResult<TItem>>;
  enabled?: boolean;
}

export function useTokenPagedInfiniteQuery<TItem>({
  queryKey,
  fetchPage,
  enabled = true,
}: UseTokenPagedInfiniteQueryOptions<TItem>) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
    enabled,
    placeholderData: keepPreviousData,
  });

  const items = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  return { ...query, items };
}
